import { createContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';

export interface Company {
  id: string;
  name: string;
  isActive: boolean;
  role?: string;
}

export interface CompanyContextType {
  currentCompany: Company | null;
  companies: Company[];
  loading: boolean;
  setCurrentCompany: (company: Company) => void;
  searchCompanies: (query: string) => Promise<Company[]>;
  refetch: () => Promise<void>;
}

export const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

const COMPANY_STORAGE_KEY = 'ai-dep-current-company';

interface CompanyProviderProps {
  children: ReactNode;
}

export function CompanyProvider({ children }: CompanyProviderProps) {
  const { user, session, supabase } = useAuth();
  const [currentCompany, setCurrentCompanyState] = useState<Company | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCompanies = useCallback(async () => {
    if (!user || !session) {
      setLoading(false);
      return;
    }

    try {
      if (user.isSuperadmin) {
        // Superadmin: fetch first company or from localStorage
        const savedCompanyId = localStorage.getItem(COMPANY_STORAGE_KEY);

        if (savedCompanyId) {
          const { data: company } = await supabase
            .from('clients')
            .select('id, name, is_active')
            .eq('id', savedCompanyId)
            .single();

          if (company) {
            setCurrentCompanyState({
              id: company.id,
              name: company.name,
              isActive: company.is_active,
            });
          }
        } else {
          // Fetch first company
          const { data: firstCompany } = await supabase
            .from('clients')
            .select('id, name, is_active')
            .limit(1)
            .single();

          if (firstCompany) {
            setCurrentCompanyState({
              id: firstCompany.id,
              name: firstCompany.name,
              isActive: firstCompany.is_active,
            });
          }
        }

        setCompanies([]);
      } else {
        // Regular user: fetch companies through customer_clients
        // RLS policy automatically filters by auth.uid() -> customers.auth_id -> customer_clients.customer_id
        const { data: customerClients, error: ccError } = await supabase
          .from('customer_clients')
          .select(`
            client_id,
            role,
            clients:client_id (
              id,
              name,
              is_active
            )
          `);

        if (ccError) {
          console.error('Error fetching customer_clients:', ccError);
        }

        if (customerClients && customerClients.length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const companyList = customerClients.map((cc: any) => {
            const clientData = cc.clients;
            return {
              id: clientData?.id || cc.client_id,
              name: clientData?.name || 'Unknown',
              isActive: clientData?.is_active ?? false,
              role: cc.role,
            };
          });

          setCompanies(companyList);

          // Restore saved company or use first
          const savedCompanyId = localStorage.getItem(COMPANY_STORAGE_KEY);
          const savedCompany = companyList.find((c: Company) => c.id === savedCompanyId);

          setCurrentCompanyState(savedCompany || companyList[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  }, [user, session, supabase]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const setCurrentCompany = (company: Company) => {
    setCurrentCompanyState(company);
    localStorage.setItem(COMPANY_STORAGE_KEY, company.id);
  };

  const searchCompanies = async (query: string): Promise<Company[]> => {
    if (!user?.isSuperadmin) return [];

    const { data } = await supabase
      .from('clients')
      .select('id, name, is_active')
      .ilike('name', `%${query}%`)
      .limit(10);

    return (data || []).map((c) => ({
      id: c.id,
      name: c.name,
      isActive: c.is_active,
    }));
  };

  return (
    <CompanyContext.Provider
      value={{
        currentCompany,
        companies,
        loading,
        setCurrentCompany,
        searchCompanies,
        refetch: fetchCompanies,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
}
